/**
 * template-fields -- Field definitions for the WSD Template Manager.
 *
 * Defines all configurable fields organized by category. Each field has a
 * type, label, description, default value, and optional server-type filter.
 * Used by TemplateManager.tsx to render the editor UI.
 */

// ============================================================================
// Types
// ============================================================================

export type FieldType = "boolean" | "number" | "string" | "string[]" | "enum";

/** Which top-level bucket the field belongs to in the template payload */
export type FieldBucket = "policy" | "config" | "wsdLocal";

export interface FieldDef {
  /** Key name matching the template payload field (e.g. "IsDisabled") */
  key: string;
  /** Which bucket: policyFields, configFields, or wsdLocalFields */
  bucket: FieldBucket;
  /** UI type for the value control */
  type: FieldType;
  /** Display label */
  label: string;
  /** Short description */
  description: string;
  /** Default value when first included */
  defaultValue: boolean | number | string | string[];
  /** Restrict to a specific server type (null = both) */
  serverOnly?: "emby" | "jellyfin" | null;
  /** Enum options (only when type === "enum") */
  enumOptions?: Array<{ value: string; label: string }>;
  /** Min value (only when type === "number") */
  min?: number;
  /** Max value (only when type === "number") */
  max?: number;
  /** Unit label for numbers (e.g. "bps") */
  unit?: string;
  /** Intent ID for the PATCH settings API (maps to userIntentMap.ts) */
  intentId?: string;
}

export interface FieldCategory {
  id: string;
  title: string;
  icon: string;
  accent: "amber" | "sky" | "violet" | "rose" | "slate" | "emerald" | "cyan" | "pink" | "indigo" | "teal" | "orange";
  fields: FieldDef[];
}

// ============================================================================
// Subtitle mode enum options
// ============================================================================

const subtitleModeOptions: Array<{ value: string; label: string }> = [
  { value: "Default", label: "Default" },
  { value: "Always", label: "Always Show" },
  { value: "OnlyForced", label: "Only Forced" },
  { value: "None", label: "None" },
  { value: "Smart", label: "Smart" },
];

const introSkipOptions: Array<{ value: string; label: string }> = [
  { value: "None", label: "None" },
  { value: "ShowButton", label: "Show Button" },
  { value: "AutoSkip", label: "Auto Skip" },
];

// ============================================================================
// Category definitions
// ============================================================================

export const TEMPLATE_CATEGORIES: FieldCategory[] = [
  {
    id: "account",
    title: "Account Status",
    icon: "lucide:shield",
    accent: "amber",
    fields: [
      { key: "IsDisabled", bucket: "policy", type: "boolean", label: "Account Disabled", description: "Prevents the user from signing in", defaultValue: false, intentId: "disabled" },
      { key: "IsHidden", bucket: "policy", type: "boolean", label: "Hidden User", description: "Hides the user from login screens and user lists", defaultValue: false, intentId: "hidden" },
      { key: "IsHiddenRemotely", bucket: "policy", type: "boolean", label: "Hidden Remotely", description: "Hides the user from remote login screens", defaultValue: false, serverOnly: "emby", intentId: "hiddenRemotely" },
      { key: "IsHiddenFromUnusedDevices", bucket: "policy", type: "boolean", label: "Hidden from New Devices", description: "Hides the user on devices they have not used before", defaultValue: false, serverOnly: "emby", intentId: "hiddenFromNewDevices" },
    ],
  },
  {
    id: "access",
    title: "Access & Streaming",
    icon: "lucide:play-circle",
    accent: "sky",
    fields: [
      { key: "EnableRemoteAccess", bucket: "policy", type: "boolean", label: "Remote Access", description: "Allow this user to connect remotely", defaultValue: true, intentId: "remoteAccess" },
      { key: "EnableMediaPlayback", bucket: "policy", type: "boolean", label: "Media Playback", description: "Allow media playback on the server", defaultValue: true, intentId: "allowPlayback" },
      { key: "SimultaneousStreamLimit", bucket: "policy", type: "number", label: "Stream Limit", description: "Maximum simultaneous streams (0 = unlimited)", defaultValue: 0, min: 0, max: 20, intentId: "simultaneousStreams" },
      { key: "RemoteClientBitrateLimit", bucket: "policy", type: "number", label: "Bitrate Limit", description: "Remote client bitrate limit (0 = unlimited)", defaultValue: 0, min: 0, unit: "bps", intentId: "bitrateLimit" },
    ],
  },
  {
    id: "transcoding",
    title: "Playback & Transcoding",
    icon: "lucide:cpu",
    accent: "violet",
    fields: [
      { key: "EnableAudioPlaybackTranscoding", bucket: "policy", type: "boolean", label: "Audio Transcoding", description: "Allow audio transcoding during playback", defaultValue: true, intentId: "audioTranscoding" },
      { key: "EnableVideoPlaybackTranscoding", bucket: "policy", type: "boolean", label: "Video Transcoding", description: "Allow video transcoding during playback", defaultValue: true, intentId: "videoTranscoding" },
      { key: "EnablePlaybackRemuxing", bucket: "policy", type: "boolean", label: "Remuxing", description: "Allow container remuxing during playback", defaultValue: true, intentId: "remuxing" },
      { key: "EnableSyncTranscoding", bucket: "policy", type: "boolean", label: "Sync Transcoding", description: "Allow transcoding for offline sync", defaultValue: true, intentId: "syncTranscoding" },
      { key: "EnableMediaConversion", bucket: "policy", type: "boolean", label: "Media Conversion", description: "Allow media format conversion", defaultValue: true, intentId: "mediaConversion" },
      { key: "AutoRemoteQuality", bucket: "policy", type: "number", label: "Auto Remote Quality", description: "Automatic quality setting for remote playback", defaultValue: 0, min: 0, serverOnly: "emby", intentId: "autoRemoteQuality" },
    ],
  },
  {
    id: "library",
    title: "Library Access",
    icon: "lucide:library",
    accent: "emerald",
    fields: [
      { key: "EnableAllFolders", bucket: "policy", type: "boolean", label: "Access All Libraries", description: "Grant access to all media libraries", defaultValue: true, intentId: "allFolders" },
      { key: "EnabledFolderNames", bucket: "policy", type: "string[]", label: "Allowed Libraries", description: "Specific library names to allow access", defaultValue: [], intentId: "enabledFolders" },
      { key: "ExcludedSubFolders", bucket: "policy", type: "string[]", label: "Excluded Sub-Folders", description: "Exclude specific media locations within libraries (e.g., 4K folders)", defaultValue: [], intentId: "excludedSubFolders", serverOnly: "emby" },
      { key: "EnableAllChannels", bucket: "policy", type: "boolean", label: "Access All Channels", description: "Grant access to all channels", defaultValue: true, intentId: "allChannels" },
    ],
  },
  {
    id: "content",
    title: "Content Management",
    icon: "lucide:folder",
    accent: "teal",
    fields: [
      { key: "EnableContentDeletion", bucket: "policy", type: "boolean", label: "Allow Deletion", description: "Allow this user to delete content from the server", defaultValue: false, intentId: "allowDeletion" },
      { key: "EnableContentDownloading", bucket: "policy", type: "boolean", label: "Allow Downloads", description: "Allow this user to download content", defaultValue: true, intentId: "allowDownload" },
      { key: "EnableSubtitleDownloading", bucket: "policy", type: "boolean", label: "Subtitle Downloads", description: "Allow downloading external subtitles", defaultValue: true, intentId: "subtitleDownloading" },
      { key: "EnableSubtitleManagement", bucket: "policy", type: "boolean", label: "Subtitle Management", description: "Allow managing subtitles on the server", defaultValue: false, intentId: "subtitleManagement" },
    ],
  },
  {
    id: "parental",
    title: "Parental Controls",
    icon: "lucide:baby",
    accent: "rose",
    fields: [
      { key: "IsTagBlockingModeInclusive", bucket: "policy", type: "boolean", label: "Tag Mode: Allowlist", description: "Use inclusive tag blocking mode", defaultValue: false, intentId: "tagBlockingInclusive" },
      { key: "AllowTagOrRating", bucket: "policy", type: "boolean", label: "Allow if Tag OR Rating passes", description: "Allow content if either tag or rating check passes", defaultValue: false, serverOnly: "emby", intentId: "allowTagOrRating" },
      { key: "MaxParentalRating", bucket: "policy", type: "number", label: "Max Rating", description: "Maximum allowed parental rating (0 = none)", defaultValue: 0, min: 0, intentId: "maxParentalRating" },
      { key: "BlockedTags", bucket: "policy", type: "string[]", label: "Blocked Tags", description: "Tags to block from this user", defaultValue: [], intentId: "blockedTags" },
      { key: "IncludeTags", bucket: "policy", type: "string[]", label: "Include Tags", description: "Tags to include for this user", defaultValue: [], intentId: "includeTags" },
      { key: "BlockUnratedItems", bucket: "policy", type: "string[]", label: "Block Unrated Items", description: "Content types to block when unrated", defaultValue: [], intentId: "blockUnratedItems" },
    ],
  },
  {
    id: "social",
    title: "Social & Sharing",
    icon: "lucide:share-2",
    accent: "pink",
    fields: [
      { key: "EnablePublicSharing", bucket: "policy", type: "boolean", label: "Public Sharing", description: "Allow public media sharing links", defaultValue: false, intentId: "publicSharing" },
      { key: "AllowSharingPersonalItems", bucket: "policy", type: "boolean", label: "Share Personal Items", description: "Allow sharing personal media items", defaultValue: false, serverOnly: "emby", intentId: "sharePersonalItems" },
      { key: "AllowCameraUpload", bucket: "policy", type: "boolean", label: "Camera Upload", description: "Allow uploading photos from device camera", defaultValue: false, serverOnly: "emby", intentId: "cameraUpload" },
    ],
  },
  {
    id: "livetv",
    title: "Live TV",
    icon: "lucide:tv",
    accent: "cyan",
    fields: [
      { key: "EnableLiveTvAccess", bucket: "policy", type: "boolean", label: "Live TV Access", description: "Allow access to live TV streams", defaultValue: true, intentId: "liveTvAccess" },
      { key: "EnableLiveTvManagement", bucket: "policy", type: "boolean", label: "Live TV Management", description: "Allow managing live TV settings", defaultValue: false, intentId: "liveTvManagement" },
    ],
  },
  {
    id: "features",
    title: "Features & Control",
    icon: "lucide:settings",
    accent: "indigo",
    fields: [
      { key: "EnableRemoteControlOfOtherUsers", bucket: "policy", type: "boolean", label: "Remote Control Others", description: "Allow remote controlling other users' sessions", defaultValue: false, intentId: "remoteControlOthers" },
      { key: "EnableSharedDeviceControl", bucket: "policy", type: "boolean", label: "Shared Device Control", description: "Allow controlling shared devices", defaultValue: true, intentId: "sharedDeviceControl" },
      { key: "EnableUserPreferenceAccess", bucket: "policy", type: "boolean", label: "User Preference Access", description: "Allow access to user preferences", defaultValue: true, intentId: "userPreferenceAccess" },
      { key: "RestrictedFeatures", bucket: "policy", type: "string[]", label: "Restricted Features", description: "List of restricted feature names", defaultValue: [], serverOnly: "emby", intentId: "restrictedFeatures" },
    ],
  },
  {
    id: "preferences",
    title: "Preferences (Config)",
    icon: "lucide:sliders-horizontal",
    accent: "slate",
    fields: [
      { key: "AudioLanguagePreference", bucket: "config", type: "string", label: "Audio Language", description: "Preferred audio language (e.g. eng, fre)", defaultValue: "", intentId: "audioLanguage" },
      { key: "SubtitleLanguagePreference", bucket: "config", type: "string", label: "Subtitle Language", description: "Preferred subtitle language (e.g. eng, fre)", defaultValue: "", intentId: "subtitleLanguage" },
      { key: "SubtitleMode", bucket: "config", type: "enum", label: "Subtitle Mode", description: "Default subtitle display mode", defaultValue: "Default", enumOptions: subtitleModeOptions, intentId: "subtitleMode" },
      { key: "PlayDefaultAudioTrack", bucket: "config", type: "boolean", label: "Play Default Audio", description: "Play the default audio track automatically", defaultValue: true, intentId: "playDefaultAudio" },
      { key: "DisplayMissingEpisodes", bucket: "config", type: "boolean", label: "Show Missing Episodes", description: "Show missing episodes in series views", defaultValue: false, intentId: "showMissingEpisodes" },
      { key: "EnableNextEpisodeAutoPlay", bucket: "config", type: "boolean", label: "Auto-play Next", description: "Automatically play the next episode", defaultValue: true, intentId: "autoPlayNext" },
      { key: "HidePlayedInLatest", bucket: "config", type: "boolean", label: "Hide Played in Latest", description: "Hide already-played items from latest media", defaultValue: false, intentId: "hidePlayedInLatest" },
      { key: "RememberAudioSelections", bucket: "config", type: "boolean", label: "Remember Audio", description: "Remember audio track selections across playback", defaultValue: true, intentId: "rememberAudio" },
      { key: "RememberSubtitleSelections", bucket: "config", type: "boolean", label: "Remember Subtitles", description: "Remember subtitle selections across playback", defaultValue: true, intentId: "rememberSubtitles" },
      { key: "IntroSkipMode", bucket: "config", type: "enum", label: "Intro Skip", description: "Behavior for skipping intros", defaultValue: "None", enumOptions: introSkipOptions, serverOnly: "emby", intentId: "introSkipMode" },
    ],
  },
  {
    id: "wsd",
    title: "WSD Enforcement",
    icon: "lucide:zap",
    accent: "orange",
    fields: [
      { key: "bypassKillStream", bucket: "wsdLocal", type: "boolean", label: "Bypass Kill Stream", description: "Exempt from pause warnings and automatic stops", defaultValue: false, intentId: "bypassKillStream" },
    ],
  },
];
